import { useEffect, useState } from 'react';
import { getRecurring, type RecurringCharge } from '../api.js';
import { EmptyState, Group, IconTile, MoreRow, Row } from '../ui/index.js';
import { dollars, isUncategorized, money, shortDate } from './format.js';
import { SkeletonGroup } from './parts.js';
import { useCategoryTree } from './tree.js';

/** Recurring — charges the same merchant bills about the same amount, again and again. */
export default function Recurring(): JSX.Element {
  const cats = useCategoryTree();
  const [rows, setRows] = useState<RecurringCharge[]>([]);
  const [loading, setLoading] = useState(true);
  const [all, setAll] = useState(false);

  useEffect(() => {
    void (async () => {
      try {
        setRows(await getRecurring(180));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return <SkeletonGroup />;
  if (rows.length === 0) {
    return (
      <EmptyState
        icon="repeat"
        title="Nothing recurring yet"
        hint="A charge shows up once the same merchant bills about the same amount three times."
      />
    );
  }

  const monthly = rows.reduce((sum, r) => sum + r.cents, 0);
  const shown = all ? rows : rows.slice(0, 12);

  return (
    <Group
      footer={`${dollars(monthly)}/mo · ${dollars(monthly * 12)}/yr if each repeats monthly. A charge shows up once the same merchant bills about the same amount three times.`}
    >
      {shown.map((r) => (
        <Row
          key={`${r.merchant}:${r.cents}`}
          leading={<IconTile name="repeat" color="#5e5ce6" />}
          title={r.merchant}
          sub={`${isUncategorized(r.category) ? `${r.count}× seen` : cats.leaf(r.category)} · last ${shortDate(r.lastSeen)}`}
          detail={<span className="hb-amount">{money(r.cents)}</span>}
        />
      ))}
      {!all && <MoreRow count={rows.length - shown.length} onClick={() => setAll(true)} />}
    </Group>
  );
}
